import React, { useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// PhoneInput — Champ téléphone Guinée (+224)
// ─────────────────────────────────────────────────────────────────────────────
// • Préfixe « +224 » verrouillé, affiché en chip à gauche.
// • Saisie : chiffres uniquement, max 9, doit commencer par 6 (donc plage
//   600 000 000 → 699 999 999).
// • Stockage (valeur remontée via onChange) : chaîne canonique « +224XXXXXXXXX »
//   ou chaîne vide si l'utilisateur efface tout (utile pour les champs
//   optionnels).
// • Compatibilité ascendante : si la valeur reçue est dans un autre format
//   (« 600000000 », « +224 600 00 00 00 », « 224600000000 »...), on extrait
//   les 9 chiffres locaux et on les ré-affiche proprement.
// ─────────────────────────────────────────────────────────────────────────────

const INDICATIF = '+224';

// Extrait les 9 derniers chiffres locaux à partir de n'importe quelle valeur
// stockée (rétro-compatibilité avec les données déjà saisies sans préfixe).
function extraireDigitsLocaux(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  // Si la valeur commence par l'indicatif 224, on l'enlève.
  const sansIndicatif = digits.startsWith('224') ? digits.slice(3) : digits;
  return sansIndicatif.slice(0, 9);
}

// Formate visuellement : « 6XX XX XX XX » (espaces toutes les 2 chiffres
// après le premier groupe de 3, façon ATPM Guinée).
function formaterAffichage(digits) {
  if (!digits) return '';
  const d = digits.slice(0, 9);
  const parts = [];
  if (d.length > 0) parts.push(d.slice(0, 3));
  if (d.length > 3) parts.push(d.slice(3, 5));
  if (d.length > 5) parts.push(d.slice(5, 7));
  if (d.length > 7) parts.push(d.slice(7, 9));
  return parts.join(' ');
}

// Validation : exactement 9 chiffres ET commence par 6.
export function isPhoneValide(raw) {
  const d = extraireDigitsLocaux(raw);
  return d.length === 9 && d.startsWith('6');
}

// Renvoie « +224 6XX XX XX XX » pour l'affichage (lecture seule)
export function formaterTelephone(raw) {
  const d = extraireDigitsLocaux(raw);
  if (!d) return '';
  return `${INDICATIF} ${formaterAffichage(d)}`;
}

export default function PhoneInput({
  label,
  value,
  onChange,
  required = false,
  error,
  placeholder = '6xx xx xx xx',
  disabled = false,
  light = false,           // thème clair (admin/Paramètres)
  className = '',
  ...rest
}) {
  // Digits locaux affichés (re-extraits à chaque render depuis la valeur
  // stockée → composant 100 % contrôlé, pas de désynchronisation).
  const digits = useMemo(() => extraireDigitsLocaux(value), [value]);
  const affichage = formaterAffichage(digits);

  // Validation visuelle : on n'affiche d'erreur que si l'utilisateur a
  // commencé à taper et que la valeur est incomplète/invalide.
  const valide = digits.length === 0 || isPhoneValide(digits);
  const messageErreur =
    error
    || (digits.length > 0 && !digits.startsWith('6')
        ? 'Le numéro doit commencer par 6'
        : digits.length > 0 && digits.length < 9
          ? `Encore ${9 - digits.length} chiffre${9 - digits.length > 1 ? 's' : ''}`
          : '');

  const handleChange = (e) => {
    // Ne garder que les chiffres saisis par l'utilisateur (collage compris).
    let saisie = e.target.value.replace(/\D/g, '');

    // L'utilisateur a peut-être collé « 224600... » ou « 0600... » :
    // on enlève un éventuel indicatif 224 ou un 0 initial.
    if (saisie.startsWith('224')) saisie = saisie.slice(3);
    if (saisie.startsWith('0'))   saisie = saisie.slice(1);

    // Premier chiffre : forcer 6. Si l'utilisateur tape un autre chiffre en
    // tête, on rejette silencieusement (sauf si le champ est vide → autorisé
    // pour permettre l'effacement complet).
    if (saisie.length > 0 && saisie[0] !== '6') {
      // On tronque à zéro pour ne pas accepter un préfixe invalide.
      saisie = '';
    }

    // Limiter à 9 chiffres.
    saisie = saisie.slice(0, 9);

    // Valeur canonique remontée au parent : « +224XXXXXXXXX » (sans espaces).
    // Si l'utilisateur a tout effacé, on remonte une chaîne vide pour que les
    // champs optionnels restent vraiment vides.
    const canonique = saisie.length === 0 ? '' : `${INDICATIF}${saisie}`;

    onChange({ target: { value: canonique } });
  };

  // Classes selon le thème
  const cls = light ? {
    label    : 'block text-sm font-medium text-gray-700 mb-1',
    wrapper  : `flex items-stretch rounded-lg border ${!valide ? 'border-red-400' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-indigo-400 transition-colors bg-white overflow-hidden`,
    prefix   : 'flex items-center px-3 bg-gray-50 border-r border-gray-300 text-gray-600 text-sm font-medium select-none',
    input    : 'flex-1 bg-transparent text-gray-900 px-3 py-2 focus:outline-none placeholder-gray-400 text-sm tracking-wide',
    errorOk  : 'text-gray-500',
    errorKo  : 'text-red-500',
  } : {
    label    : 'block text-sm text-gray-400',
    wrapper  : `flex items-stretch rounded-lg border ${!valide ? 'border-red-500' : 'border-gray-700'} focus-within:border-yellow-500 transition-colors bg-gray-800 overflow-hidden`,
    prefix   : 'flex items-center px-3 bg-gray-900/60 border-r border-gray-700 text-gray-400 text-sm font-medium select-none',
    input    : 'flex-1 bg-transparent text-white px-3 py-2.5 focus:outline-none placeholder-gray-600 text-sm tracking-wide',
    errorOk  : 'text-gray-500',
    errorKo  : 'text-red-400',
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className={cls.label}>
          {label}{required && <span className={light ? 'text-red-500' : 'text-yellow-500'}> *</span>}
        </label>
      )}
      <div className={cls.wrapper}>
        <span className={cls.prefix}>
          🇬🇳 {INDICATIF}
        </span>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          value={affichage}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={12}            /* 9 chiffres + 3 espaces */
          aria-invalid={!valide}
          className={`${cls.input} ${className}`}
          {...rest}
        />
      </div>
      {messageErreur && (
        <p className={`text-xs ${valide ? cls.errorOk : cls.errorKo}`}>
          {messageErreur}
        </p>
      )}
    </div>
  );
}
